import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_Z_INDEX = 99999;
const MARGIN = 10;
const VIEWPORT_PAD = 8;

interface FloatingChartTooltipProps {
  open: boolean;
  anchorRect: DOMRect | null;
  children: ReactNode;
  preferredPlacement?: 'top' | 'bottom';
}

export function FloatingChartTooltip({
  open,
  anchorRect,
  children,
  preferredPlacement = 'top',
}: FloatingChartTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorRect || !tooltipRef.current) {
      setCoords(null);
      return;
    }

    const measure = () => {
      const tooltipEl = tooltipRef.current;
      if (!tooltipEl) return;

      const tooltipRect = tooltipEl.getBoundingClientRect();
      const spaceAbove = anchorRect.top;
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const needed = tooltipRect.height + MARGIN + VIEWPORT_PAD;

      const placeAbove =
        preferredPlacement === 'top'
          ? spaceAbove >= needed || spaceAbove >= spaceBelow
          : spaceBelow < needed && spaceAbove > spaceBelow;

      const top = placeAbove
        ? anchorRect.top - tooltipRect.height - MARGIN
        : anchorRect.bottom + MARGIN;

      let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      left = Math.max(
        VIEWPORT_PAD,
        Math.min(left, window.innerWidth - tooltipRect.width - VIEWPORT_PAD),
      );

      const clampedTop = Math.max(
        VIEWPORT_PAD,
        Math.min(top, window.innerHeight - tooltipRect.height - VIEWPORT_PAD),
      );

      setCoords({ top: clampedTop, left });
    };

    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, anchorRect, preferredPlacement, children]);

  if (!open || !anchorRect) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className="pointer-events-none fixed"
      style={{
        zIndex: TOOLTIP_Z_INDEX,
        ...(coords ? { top: coords.top, left: coords.left } : { top: -9999, left: -9999 }),
      }}
      role="tooltip"
    >
      {children}
    </div>,
    document.body,
  );
}
